import { useLocation } from 'react-router-dom';

import { scopeConfidence } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { locateSurface } from '../routes/surfaces';

/**
 * Breadcrumb on the left, scope chip on the right — the mock's `.top` row.
 *
 * The scope chip is the one piece of chrome that is always visible, because
 * "which parties am I looking at?" changes the meaning of every number on every
 * surface. It is *what this page can see of the sign-in*, not the scope the
 * server applies, and not a control: the SPA cannot tell which of the token's
 * groups are CLM scope groups (see below), and the user cannot change scope
 * here, or anywhere, because it is derived from Entra group claims server-side
 * and is never a client parameter (NFR-06). The API is the only authority on
 * the applied scope; the claims card the chip opens says so.
 */
export function TopBar({
  claimsOpen,
  onToggleClaims,
}: {
  readonly claimsOpen: boolean;
  readonly onToggleClaims: () => void;
}) {
  const { pathname } = useLocation();
  const { identity, isAuthenticated } = useIdentity();
  const located = locateSurface(pathname);
  const confidence = scopeConfidence(identity, isAuthenticated);

  /*
   * 'scoped' counts every group in the token, and says so. It used to read
   * "Scope: 15 groups" — on 30 Sep 2026 for a user in fifteen groups, none of
   * them a CEQ-CLM-Scope-* group, whom the gateway refused on every call for
   * having no scope. The SPA cannot count scope groups: its only bindings are
   * the four role groups (`VITE_ENTRA_GROUP_CLM_*`, env.ts), the claim carries
   * object ids, and the scope-group ids live in the API's configuration alone.
   * So the label states what the page does know, and the title says who knows
   * the rest. A true count would need scope-group bindings beside the role
   * ones — new VITE_ variables, deploy-web.yml, repository variables — which
   * is a deployment change, not a copy fix; and even then they could only
   * ever be display, never a party/practice filter (permissions.ts, NFR-06).
   */
  const groupCount = identity.scope_groups.length;
  const scopeLabel =
    confidence === 'scoped'
      ? `${groupCount} group${groupCount === 1 ? '' : 's'} in your sign-in`
      : confidence === 'none'
        ? 'Scope: none assigned'
        : 'Scope: resolved by the API';
  const scopeTitle =
    confidence === 'scoped'
      ? 'Your CLM scope is resolved by the API from the CEQ-CLM-Scope-* groups among these. This page cannot tell which of them those are.'
      : undefined;

  return (
    <div className="top">
      <span className="crumb">
        {located ? (
          <>
            {located.group.label} / <b>{located.surface.label}</b>
          </>
        ) : (
          <b>Not found</b>
        )}
      </span>
      <span className="sp" />
      {/* The neutral dot in every state. The green one (`.scope i`, --ok)
          read as "scope healthy" beside "15 groups in your sign-in" while the
          gateway refused every call for no scope (30 Sep 2026); the SPA cannot
          know scope is present, so it never claims it. Green is left for a
          scope the API itself has confirmed. */}
      <button
        className="scope unknown"
        type="button"
        aria-expanded={claimsOpen}
        title={scopeTitle}
        onClick={onToggleClaims}
      >
        <i />
        {scopeLabel}
      </button>
    </div>
  );
}
